// Tiny API client. All requests carry the admin session cookie via
// `credentials: 'include'`. Throws ApiError on non-2xx with a structured shape
// so pages can show friendly messages.

import type { Ir } from '@scriptvoice/ir';

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(
  path: string,
  init?: RequestInit & { json?: unknown },
): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set('X-Requested-With', 'scriptvoice-spa');
  if (init?.json !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(path, {
    ...init,
    headers,
    credentials: 'include',
    body: init?.json !== undefined ? JSON.stringify(init.json) : init?.body,
  });
  const ct = res.headers.get('Content-Type') || '';
  let body: unknown = null;
  if (ct.includes('application/json')) {
    body = await res.json().catch(() => null);
  } else {
    body = await res.text().catch(() => '');
  }
  if (!res.ok) {
    const err = (body as { error?: string; code?: string }) || {};
    throw new ApiError(res.status, err.code ?? 'UNKNOWN', err.error ?? `HTTP ${res.status}`);
  }
  return body as T;
}

// ─── Auth ─────────────────────────────────────────────────────────────────
// Admin auth (legacy single password) — still used for /admin-login.
export const auth = {
  me: () => request<{ ok: true }>('/api/admin/me'),
  login: (password: string) =>
    request<{ ok: true; expires_at: string }>('/api/admin/login', {
      method: 'POST',
      json: { password },
    }),
  logout: () => request<{ ok: true }>('/api/admin/logout', { method: 'POST' }),
};

// ─── User accounts (Phase 7) ──────────────────────────────────────────────
export interface PublicUser {
  id: string;
  email: string;
  display_name: string | null;
  email_verified: boolean;
  created_at: string;
}

export const userAuth = {
  me: () => request<{ user: PublicUser }>('/api/auth/me'),
  signup: (email: string, password: string, displayName?: string) =>
    request<{ user: PublicUser; expires_at: string }>('/api/auth/signup', {
      method: 'POST',
      json: { email, password, display_name: displayName },
    }),
  login: (email: string, password: string) =>
    request<{ user: PublicUser; expires_at: string }>('/api/auth/login', {
      method: 'POST',
      json: { email, password },
    }),
  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  verifyEmail: (token: string) =>
    request<{ ok: true }>(`/api/auth/verify-email?token=${encodeURIComponent(token)}`),
  resendVerification: () =>
    request<{ ok: true; already_verified?: boolean }>('/api/auth/resend-verification', { method: 'POST' }),
  forgotPassword: (email: string) =>
    request<{ ok: true }>('/api/auth/forgot-password', {
      method: 'POST',
      json: { email },
    }),
  resetPassword: (token: string, newPassword: string) =>
    request<{ ok: true }>('/api/auth/reset-password', {
      method: 'POST',
      json: { token, new_password: newPassword },
    }),
};

// User-scoped keys (Phase 7c). Same shape as `keys` but hits /api/keys/*.
export const userKeys = {
  list: () => request<{ keys: StoredKeySummary[] }>('/api/keys'),
  upsert: (provider: string, apiKey: string) =>
    request<{ key: StoredKeySummary }>('/api/keys', {
      method: 'POST',
      json: { provider, api_key: apiKey },
    }),
  remove: (provider: string) =>
    request<{ ok: boolean; removed: boolean }>(`/api/keys/${provider}`, {
      method: 'DELETE',
    }),
};

// ─── Keys ─────────────────────────────────────────────────────────────────
export interface StoredKeySummary {
  id: string;
  provider_id: string;
  masked_preview: string;
  last_validated_at: string | null;
  created_at: string;
  updated_at: string;
}
export const keys = {
  list: () => request<{ keys: StoredKeySummary[] }>('/api/admin/keys'),
  upsert: (provider: string, apiKey: string) =>
    request<{ key: StoredKeySummary }>('/api/admin/keys', {
      method: 'POST',
      json: { provider, api_key: apiKey },
    }),
  remove: (provider: string) =>
    request<{ ok: boolean; removed: boolean }>(`/api/admin/keys/${provider}`, {
      method: 'DELETE',
    }),
};

// ─── Projects ─────────────────────────────────────────────────────────────
export interface ProjectSummary {
  id: string;
  title: string;
  word_count: number;
  has_ir: boolean;
  last_rendered_at: string | null;
  last_render_provider: string | null;
  last_render_voice_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectFull {
  id: string;
  title: string;
  script_text: string;
  intent: string | null;
  tone_preference: string | null;
  ir: Ir | null;
  director_provider: string | null;
  director_model: string | null;
  prompt_version: string | null;
  last_render_id: string | null;
  last_render_provider: string | null;
  last_render_voice_id: string | null;
  last_render_size_bytes: number | null;
  last_rendered_at: string | null;
  lufs_target: number | null;
  crossfade_ms: number | null;
  created_at: string;
  updated_at: string;
}

export type DirectorChoice = 'deepseek' | 'gemini' | 'anthropic' | 'openai';
export type TtsProvider =
  | 'openai'
  | 'gemini'
  | 'kokoro'
  | 'elevenlabs'
  | 'azure'
  | 'playht'
  | 'cartesia'
  | 'deepgram'
  | 'fish'
  | 'chatterbox'
  | 'orpheus'
  | 'qwen_tts'
  | 'dia';

export const TTS_PROVIDERS: ReadonlyArray<{
  id: TtsProvider;
  label: string;
  hint: string;
  type: 'commercial' | 'self_hosted';
  voice_cloning: 'none' | 'zero_shot' | 'instant';
}> = [
  { id: 'openai',     label: 'OpenAI',         hint: 'gpt-4o-mini-tts, fast and expressive',  type: 'commercial', voice_cloning: 'none' },
  { id: 'gemini',     label: 'Gemini',         hint: '2.5 Flash TTS, free in preview',        type: 'commercial', voice_cloning: 'none' },
  { id: 'elevenlabs', label: 'ElevenLabs',     hint: 'v3 — most expressive, paid',            type: 'commercial', voice_cloning: 'instant' },
  { id: 'azure',      label: 'Azure',          hint: 'Microsoft Neural TTS',                  type: 'commercial', voice_cloning: 'none' },
  { id: 'cartesia',   label: 'Cartesia Sonic', hint: 'sonic-3.5, 40 ms TTFA',                 type: 'commercial', voice_cloning: 'instant' },
  { id: 'deepgram',   label: 'Deepgram Aura',  hint: 'aura-2, low latency',                   type: 'commercial', voice_cloning: 'none' },
  { id: 'fish',       label: 'Fish Audio',     hint: 'S2 Pro, emotion tags',                  type: 'commercial', voice_cloning: 'zero_shot' },
  { id: 'playht',     label: 'PlayHT (dead)',  hint: 'shut down Dec 2025 — kept for legacy',  type: 'commercial', voice_cloning: 'none' },
  { id: 'kokoro',     label: 'Kokoro',         hint: 'self-hosted, free',                     type: 'self_hosted', voice_cloning: 'none' },
  { id: 'chatterbox', label: 'Chatterbox',     hint: 'self-hosted, voice cloning',            type: 'self_hosted', voice_cloning: 'zero_shot' },
  { id: 'orpheus',    label: 'Orpheus',        hint: 'self-hosted, emotion tags',             type: 'self_hosted', voice_cloning: 'none' },
  { id: 'qwen_tts',   label: 'Qwen-TTS',       hint: 'self-hosted, multilingual',             type: 'self_hosted', voice_cloning: 'zero_shot' },
  { id: 'dia',        label: 'Dia',            hint: 'self-hosted, nonverbal tags',           type: 'self_hosted', voice_cloning: 'none' },
] as const;

export const projects = {
  list: () => request<{ projects: ProjectSummary[] }>('/api/projects'),
  get: (id: string) => request<{ project: ProjectFull }>(`/api/projects/${id}`),
  create: (input: {
    title: string;
    script: string;
    intent?: string;
    tone_preference?: string;
  }) => request<{ project: ProjectFull }>('/api/projects', { method: 'POST', json: input }),
  update: (
    id: string,
    input: { title?: string; script?: string; intent?: string; tone_preference?: string },
  ) => request<{ project: ProjectFull }>(`/api/projects/${id}`, { method: 'PATCH', json: input }),
  remove: (id: string) =>
    request<{ ok: boolean; removed: boolean }>(`/api/projects/${id}`, { method: 'DELETE' }),
  direct: (id: string, model: DirectorChoice) =>
    request<{ ir: Ir; provider: string; model: string; prompt_version: string }>(
      `/api/projects/${id}/direct`,
      { method: 'POST', json: { model } },
    ),
  render: (id: string, provider: TtsProvider, voiceId: string) =>
    request<{
      render_id: string;
      audio_url: string;
      provider: string;
      voice_id: string;
      size_bytes: number;
      report: { honoured: string[]; approximated: string[]; dropped: string[]; notes: string[] };
    }>(`/api/projects/${id}/render`, { method: 'POST', json: { provider, voice_id: voiceId } }),
  renderAsync: (id: string, provider: TtsProvider, voiceId: string, onlySectionIds?: string[]) =>
    request<{ job_id: string; events_url: string; status_url: string }>(
      `/api/projects/${id}/render-async`,
      { method: 'POST', json: { provider, voice_id: voiceId, only_section_ids: onlySectionIds } },
    ),
  putIr: (id: string, ir: Ir) =>
    request<{ ir: Ir }>(`/api/projects/${id}/ir`, { method: 'PUT', json: { ir } }),
  redirectSection: (id: string, sectionId: string, tone?: string) =>
    request<{ ir: Ir; regenerated_section_id: string; tone: string | null; provider: string; model: string }>(
      `/api/projects/${id}/redirect-section`,
      { method: 'POST', json: { section_id: sectionId, tone } },
    ),
  regenSectionRender: (id: string, sectionIds: string[], provider?: TtsProvider, voiceId?: string) =>
    request<{
      render_id: string;
      audio_url: string;
      provider: string;
      voice_id: string;
      size_bytes: number;
      sections_rerendered: string[];
      sections_cached: string[];
    }>(`/api/projects/${id}/regen-section-render`, {
      method: 'POST',
      json: { section_ids: sectionIds, provider, voice_id: voiceId },
    }),
  updateAudioSettings: (id: string, lufsTarget: number | null, crossfadeMs: number | null) =>
    request<{ project: ProjectFull }>(`/api/projects/${id}/audio-settings`, {
      method: 'PATCH',
      json: { lufs_target: lufsTarget, crossfade_ms: crossfadeMs },
    }),
  directAsync: (id: string, model: 'deepseek' | 'gemini' | 'anthropic' | 'openai') =>
    request<{ job_id: string; events_url: string; status_url: string }>(
      `/api/projects/${id}/direct-async`,
      { method: 'POST', json: { provider: model } },
    ),
  directJobStatus: (jobId: string) =>
    request<{
      job_id: string;
      state: string;
      progress: { chunksDone: number; chunksTotal: number; etaMs: number | null; message: string } | null;
      result: { irId: string; provider: string; model: string; sectionsCount: number; chunksCount: number; durationMs: number } | null;
      error: string | null;
    }>(`/api/direct-jobs/${jobId}`),
  exportMd: (id: string) => `/api/projects/${id}/export/md`,
  exportJson: (id: string) => `/api/projects/${id}/export/json`,
  audioUrl: (renderId: string) => `/api/audio/${renderId}.mp3`,
};

// ─── Voices ───────────────────────────────────────────────────────────────
export interface VoiceDescriptor {
  id: string;
  display_name: string;
  description: string;
  gender: 'feminine' | 'masculine' | 'neutral';
  age: 'young_adult' | 'adult' | 'mature';
  accent: string | null;
  style_tags: string[];
  preview_text_default: string;
}
export const voices = {
  list: (provider: TtsProvider) =>
    request<{ provider: string; voices: VoiceDescriptor[] }>(`/api/voices/${provider}`),
};

// ─── Voice clones (Phase 51) ──────────────────────────────────────────────
export type ConsentType =
  | 'own_voice'
  | 'licensed'
  | 'public_domain'
  | 'signed_release'
  | 'ai_synthetic';

export interface VoiceCloneSummary {
  id: string;
  name: string;
  description: string | null;
  duration_ms: number;
  size_bytes: number;
  mime_type: string;
  consent_type: ConsentType;
  consent_attested_at: string;
  created_at: string;
  updated_at: string;
}

export const voiceClones = {
  list: () => request<{ clones: VoiceCloneSummary[] }>('/api/voice-clones'),
  get: (id: string) => request<{ clone: VoiceCloneSummary }>(`/api/voice-clones/${id}`),
  create: (input: {
    name: string;
    description?: string | null;
    audio_base64: string;
    mime_type: 'audio/wav' | 'audio/mpeg' | 'audio/ogg' | 'audio/flac';
    duration_ms: number;
    consent_type: ConsentType;
  }) =>
    request<{ clone: VoiceCloneSummary }>('/api/voice-clones', {
      method: 'POST',
      json: input,
    }),
  patch: (id: string, input: { name?: string; description?: string | null }) =>
    request<{ clone: VoiceCloneSummary }>(`/api/voice-clones/${id}`, {
      method: 'PATCH',
      json: input,
    }),
  remove: (id: string) =>
    request<{ ok: true }>(`/api/voice-clones/${id}`, { method: 'DELETE' }),
  sourceUrl: (id: string) => `/api/voice-clones/${id}/source`,
};

// ─── Batch processing ──────────────────────────────────────────────────────
export interface BatchSummary {
  id: string;
  user_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  item_count: number;
  completed_count: number;
  failed_count: number;
  director_model: DirectorChoice;
  tts_provider: TtsProvider | null;
  tts_voice_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface BatchItem {
  id: string;
  batch_id: string;
  index: number;
  title: string;
  script: string;
  status: 'pending' | 'directing' | 'rendering' | 'completed' | 'failed';
  project_id: string | null;
  render_id: string | null;
  error: string | null;
  started_at: string | null;
  completed_at: string | null;
}

export const batches = {
  list: () => request<{ batches: BatchSummary[] }>('/api/batch'),
  get: (id: string) =>
    request<{ batch: BatchSummary; items: BatchItem[] }>(`/api/batch/${id}`),
  status: (id: string) =>
    request<{ status: BatchSummary['status']; completed_count: number; failed_count: number; item_count: number }>(
      `/api/batch/${id}/status`,
    ),
  create: (input: {
    items: { title: string; script: string }[];
    director_model?: DirectorChoice;
    tts_provider?: TtsProvider | null;
    tts_voice_id?: string | null;
  }) => request<{ batch: BatchSummary }>('/api/batch', { method: 'POST', json: input }),
  cancel: (id: string) =>
    request<{ ok: true }>(`/api/batch/${id}/cancel`, { method: 'POST' }),
  downloadUrl: (id: string) => `/api/batch/${id}/download`,
};

// ─── Custom director prompts ───────────────────────────────────────────────
export interface CustomPrompt {
  id: string;
  user_id: string;
  name: string;
  content: string;
  is_default: boolean;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

export const customPrompts = {
  list: () => request<{ prompts: CustomPrompt[] }>('/api/prompts'),
  get: (id: string) => request<{ prompt: CustomPrompt }>(`/api/prompts/${id}`),
  create: (input: { name: string; content: string; is_default?: boolean }) =>
    request<{ prompt: CustomPrompt }>('/api/prompts', { method: 'POST', json: input }),
  patch: (id: string, input: { name?: string; content?: string; is_default?: boolean }) =>
    request<{ prompt: CustomPrompt }>(`/api/prompts/${id}`, { method: 'PATCH', json: input }),
  remove: (id: string) =>
    request<{ ok: true }>(`/api/prompts/${id}`, { method: 'DELETE' }),
};

// ─── Analytics ─────────────────────────────────────────────────────────────
export interface AnalyticsOverview {
  total_renders: number;
  total_directs: number;
  total_seconds_generated: number;
  unique_voices_used: number;
  providers_used: string[];
}

export interface DailyStat {
  date: string;
  renders: number;
  directs: number;
}

export interface ProviderStat {
  provider: string;
  renders: number;
  total_seconds: number;
}

export const analytics = {
  overview: (days = 30) => request<AnalyticsOverview>(`/api/analytics/overview?days=${days}`),
  daily: (days = 30) => request<{ daily: DailyStat[] }>(`/api/analytics/daily?days=${days}`),
  providers: (days = 30) => request<{ providers: ProviderStat[] }>(`/api/analytics/providers?days=${days}`),
};

// ─── Developer API keys ────────────────────────────────────────────────────
export interface DeveloperKey {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  last_used_at: string | null;
  request_count: number;
  created_at: string;
  revoked_at: string | null;
}

export interface CreatedDeveloperKey extends DeveloperKey {
  /** Plaintext token — shown ONCE on creation. */
  token: string;
}

export const developerKeys = {
  list: () => request<{ keys: DeveloperKey[] }>('/api/developer/keys'),
  create: (input: { name: string; scopes?: string[] }) =>
    request<{ key: CreatedDeveloperKey }>('/api/developer/keys', {
      method: 'POST',
      json: input,
    }),
  revoke: (id: string) =>
    request<{ ok: true }>(`/api/developer/keys/${id}`, { method: 'DELETE' }),
};

// ─── Project IR history (Phase 6) ──────────────────────────────────────────
export interface IrRevisionSummary {
  id: string;
  revision_number: number;
  change_type: 'direct' | 'edit' | 'redirect_section' | 'revert' | 'import';
  change_summary: string | null;
  director_provider: string | null;
  director_model: string | null;
  created_at: string;
}

export interface IrRevisionDetail extends IrRevisionSummary {
  project_id: string;
  ir: Ir;
  prompt_version: string | null;
  created_by: string | null;
}

export const irHistory = {
  list: (projectId: string) =>
    request<{ revisions: IrRevisionSummary[] }>(
      `/api/projects/${projectId}/history?limit=100`,
    ),
  get: (projectId: string, revisionId: string) =>
    request<{ revision: IrRevisionDetail }>(
      `/api/projects/${projectId}/history/${revisionId}`,
    ),
  revert: (projectId: string, revisionId: string) =>
    request<{ revision: IrRevisionDetail }>(
      `/api/projects/${projectId}/history/${revisionId}/revert`,
      { method: 'POST' },
    ),
};

// ─── Voice assignments (Phase 4 multi-voice) ───────────────────────────────
export interface VoiceAssignment {
  id: string;
  project_id: string;
  role: string;
  role_label: string | null;
  provider: string;
  voice_id: string;
  voice_name: string | null;
  created_at: string;
  updated_at: string;
}

export const voiceAssignments = {
  list: (projectId: string) =>
    request<{ assignments: VoiceAssignment[]; multi_voice: boolean }>(
      `/api/projects/${projectId}/voices`,
    ),
  upsert: (
    projectId: string,
    input: { role: string; role_label?: string | null; provider: string; voice_id: string; voice_name?: string | null },
  ) =>
    request<{ assignment: VoiceAssignment }>(`/api/projects/${projectId}/voices`, {
      method: 'PUT',
      json: input,
    }),
  remove: (projectId: string, assignmentId: string) =>
    request<{ ok: boolean }>(`/api/projects/${projectId}/voices/${assignmentId}`, {
      method: 'DELETE',
    }),
  clearAll: (projectId: string) =>
    request<{ ok: boolean; deleted: number }>(`/api/projects/${projectId}/voices`, {
      method: 'DELETE',
    }),
};
